// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import { useEffect, useRef, useState } from 'preact/hooks';
import type { SnsWebSdk } from '@sumsub/websdk';
import { api } from '../api.js';
import '../styles/kyc-modal.css';

/** The default export of `@sumsub/websdk` (`{ init, Builder }`). Imported
 *  as a *type only* so the SDK bundle stays out of the main chunk — the
 *  component dynamic-imports the real module on mount (the modal is only
 *  rendered when a user actually starts verification). */
type SnsWebSdkModule = (typeof import('@sumsub/websdk'))['default'];

/** DOM id the Sumsub iframe is launched into. */
const CONTAINER_ID = 'kyc-websdk-container';

/**
 * Wire a Sumsub WebSDK instance to our token endpoint. Factored out of
 * the component — and given the SDK module by injection — so it is
 * unit-testable without mounting the iframe.
 *
 * The second `init` argument is the WebSDK's access-token refresh
 * callback (`accessTokenExpirationHandler`): the SDK calls it when its
 * token is about to expire, and we re-issue through the *same* user-app
 * proxy endpoint that serves the initial token. productco-kyc serves
 * both initial issue and refresh from `POST /kyc/access-token`, so there
 * is nothing to branch on here.
 */
export function buildKycSdk(
  sdk: SnsWebSdkModule,
  token: string,
  onStatusMaybeChanged: () => void,
): SnsWebSdk {
  return (
    sdk
      .init(token, () => api.getKycAccessToken().then((r) => r.token))
      .withConf({ lang: 'en' })
      // Don't let the SDK rewrite the host page's viewport meta tag; the
      // SPA owns it. Height adaptation keeps the iframe sized to content.
      .withOptions({ addViewportTag: false, adaptIframeHeight: true })
      // Any of these mean the applicant's review state may have advanced
      // (submitted for review, status flipped GREEN/RED, review done) —
      // re-poll GET /kyc/status so the vault gate and this banner update.
      .on('idCheck.onApplicantSubmitted', () => onStatusMaybeChanged())
      .on('idCheck.onApplicantStatusChanged', () => onStatusMaybeChanged())
      .on('idCheck.onApplicantReviewComplete', () => onStatusMaybeChanged())
      .on('idCheck.onError', (err) => {
        // Non-fatal: the SDK renders its own error UI. Logged so a CSP or
        // allowed-origin misconfig surfaces while live-tuning the policy.
        console.error('[kyc] WebSDK error', err);
      })
      .build()
  );
}

interface KycModalProps {
  /** User dismissed the modal (close button / overlay / Esc). */
  onClose: () => void;
  /** The SDK signalled the applicant status may have changed — the
   *  caller should re-poll `GET /kyc/status` to flip the gate. */
  onStatusMaybeChanged: () => void;
}

/**
 * Phase C — the Sumsub WebSDK verification modal. Fetches a ProductCo-
 * issued access token (proxied through user-app, never touching Sumsub
 * directly), then launches the embedded WebSDK iframe. The SDK drives
 * the whole document-upload + liveness flow inside its own iframe; we
 * only react to its lifecycle events to keep `users.kyc_status` fresh.
 */
export function KycModal({ onClose, onStatusMaybeChanged }: KycModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const sdkRef = useRef<SnsWebSdk | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        // Token issue and SDK module load are independent — run together.
        const [{ token }, sdkModule] = await Promise.all([
          api.getKycAccessToken(),
          import('@sumsub/websdk'),
        ]);
        if (cancelled) return;
        const instance = buildKycSdk(sdkModule.default, token, onStatusMaybeChanged);
        sdkRef.current = instance;
        instance.launch(`#${CONTAINER_ID}`);
        setLoading(false);
      } catch {
        if (!cancelled) {
          setError('Could not start verification. Please try again.');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      try {
        sdkRef.current?.destroy();
      } catch {
        // The SDK may never have launched (token fetch failed) — ignore.
      }
      sdkRef.current = null;
    };
  }, [onStatusMaybeChanged]);

  // Esc closes the modal.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      class="kyc-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kyc-modal-title"
      onClick={onClose}
    >
      <div class="kyc-modal-card" onClick={(e) => e.stopPropagation()}>
        <div class="kyc-modal-head">
          <h2 id="kyc-modal-title">Identity verification</h2>
          <button
            type="button"
            class="kyc-modal-close"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        {loading && <p class="kyc-modal-status">Loading verification…</p>}
        {error && <p class="kyc-modal-error">{error}</p>}
        <div id={CONTAINER_ID} class="kyc-modal-websdk" />
      </div>
    </div>
  );
}
